import React, { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Database, Building, CheckCircle, AlertCircle, Eye } from 'lucide-react';

const TestSingleCompanyPage = () => {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string; details?: any } | null>(null);

  const testCompany = async (companyName: string, companyId: string) => {
    if (!supabase) return;

    setTesting(true);
    setResult(null);

    try {
      console.log(`Testing ${companyName} import...`);

      // Simple direct function call to test one company
      const { data, error } = await supabase.functions.invoke('successware-import', {
        body: {
          company_id: companyId,
          import_type: 'customers',
          batch_size: 3
        }
      });

      if (error) {
        console.error('Function error:', error);
        throw error;
      }

      console.log('Function response:', data);

      setResult({
        success: true,
        message: `Successfully tested ${companyName} import`,
        details: data
      });

    } catch (error: any) {
      console.error('Test error:', error);
      setResult({
        success: false,
        message: `${companyName} test failed: ${error.message || error}`,
        details: error
      });
    } finally {
      setTesting(false);
    }
  };

  const testDirectAPI = async () => {
    if (!supabase) return;

    setTesting(true);
    setResult(null);

    try {
      console.log('Testing direct API call...');

      // Test if function exists and responds
      const { data, error } = await supabase.functions.invoke('successware-import', {
        body: {
          test: true
        }
      });

      console.log('Direct API response:', { data, error });

      if (error) {
        throw error;
      }

      setResult({
        success: true,
        message: 'Edge Function responded successfully',
        details: { data, error }
      });

    } catch (error: any) {
      console.error('Direct API test error:', error);
      setResult({
        success: false,
        message: `Direct API test failed: ${error.message || error}`,
        details: error
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Test SuccessWare API</h1>
          <p className="text-slate-400">Test individual companies to debug the API connection</p>
        </div>

        <div className="bg-slate-800 rounded-lg p-6 mb-6">
          <div className="flex items-center mb-4">
            <Database className="h-6 w-6 text-blue-500 mr-3" />
            <h2 className="text-xl font-semibold">Test Individual Companies</h2>
          </div>

          <div className="grid grid-cols-1 gap-4 mb-6">
            <button
              onClick={testDirectAPI}
              disabled={testing}
              className="bg-purple-600 hover:bg-purple-700 disabled:bg-slate-600 text-white px-6 py-3 rounded-lg transition-colors flex items-center justify-center"
            >
              {testing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Testing...
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4 mr-2" />
                  Test Edge Function Connection
                </>
              )}
            </button>

            <button
              onClick={() => testCompany('Hales AC', 'hales-ac')}
              disabled={testing}
              className="bg-green-600 hover:bg-green-700 disabled:bg-slate-600 text-white px-6 py-3 rounded-lg transition-colors flex items-center justify-center"
            >
              {testing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Testing...
                </>
              ) : (
                <>
                  <Building className="h-4 w-4 mr-2" />
                  Test Hales AC Import
                </>
              )}
            </button>

            <button
              onClick={() => testCompany('Climate Control Services', 'climate-control')}
              disabled={testing}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 text-white px-6 py-3 rounded-lg transition-colors flex items-center justify-center"
            >
              {testing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Testing...
                </>
              ) : (
                <>
                  <Building className="h-4 w-4 mr-2" />
                  Test Climate Control Services
                </>
              )}
            </button>

            <button
              onClick={() => testCompany('Abraham AC', 'abraham-ac')}
              disabled={testing}
              className="bg-red-600 hover:bg-red-700 disabled:bg-slate-600 text-white px-6 py-3 rounded-lg transition-colors flex items-center justify-center"
            >
              {testing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Testing...
                </>
              ) : (
                <>
                  <Building className="h-4 w-4 mr-2" />
                  Test Abraham AC
                </>
              )}
            </button>
          </div>
        </div>

        {result && (
          <div className={`rounded-lg p-6 ${result.success ? 'bg-green-900/50 border border-green-700' : 'bg-red-900/50 border border-red-700'}`}>
            <div className="flex items-center mb-3">
              {result.success ? (
                <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              )}
              <h3 className="font-semibold">Test Result</h3>
            </div>
            <p className="mb-3">{result.message}</p>

            {result.details && (
              <div className="bg-slate-800 rounded p-3 max-h-96 overflow-auto">
                <pre className="text-sm text-slate-300 whitespace-pre-wrap">
                  {JSON.stringify(result.details, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}

        <div className="bg-slate-800 rounded-lg p-4 mt-6">
          <h3 className="font-semibold mb-2">Debug Steps:</h3>
          <ol className="text-sm text-slate-300 space-y-1 list-decimal list-inside">
            <li>First test Edge Function connection to see if it responds</li>
            <li>Test each company individually to identify issues</li>
            <li>Check console logs for detailed error information</li>
            <li>Verify API credentials and company tokens</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default TestSingleCompanyPage;